import {getCenter} from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import {Circle} from 'ol/geom';
import {fromExtent as polygonFromExtent} from 'ol/geom/Polygon';
import {toRadians} from 'ol/math.js';
import {fromLonLat} from 'ol/proj';
import {getArea} from 'ol/sphere';
import {trackEvent} from '@src/analytics';
import {GEO_PROJECTION, MAX_GEOMETRY_AREA} from '@src/constants';
import {SUPER_RES_AOI_AREA_RANGE} from '@src/constants/analyze';

const MAX_SIDE_LENGTH = Math.sqrt(MAX_GEOMETRY_AREA * 1e6);
const MAX_RADIUS = Math.sqrt((MAX_GEOMETRY_AREA * 1e6) / Math.PI);
const MIN_RADIUS = 10000;

function getMaxExtent(coords) {
  const half = MAX_SIDE_LENGTH / 2;
  return [
    coords[0] - half,
    coords[1] - half,
    coords[0] + half,
    coords[1] + half,
  ];
}

function getMinExtent(coords) {
  return [
    coords[0] - MIN_RADIUS,
    coords[1] - MIN_RADIUS,
    coords[0] + MIN_RADIUS,
    coords[1] + MIN_RADIUS,
  ];
}

export const MIN_AREA = Math.PI * MIN_RADIUS * MIN_RADIUS;

export function getMaxSquare(coords) {
  return polygonFromExtent(getMaxExtent(coords));
}

/**
 * Returns a square polygon representing the maximum AOI for super resolution
 * at the given center point, accounting for latitude-based Mercator projection distortion.
 * @param {import('ol/coordinate').Coordinate} centerDegrees - Center point as [longitude, latitude] in degrees (EPSG:4326)
 * @returns {import('ol/geom/Polygon').default} Square polygon in WGS84 (EPSG:4326)
 */
export function getMaxSquareForSuperRes(centerDegrees) {
  const projectionFactor = Math.cos(toRadians(Math.abs(centerDegrees[1])));
  const center = fromLonLat(centerDegrees);
  const ceaAreaBuffer = 100_000;
  const maxSideLengthInMeters =
    Math.sqrt(SUPER_RES_AOI_AREA_RANGE.max - ceaAreaBuffer) / projectionFactor;
  const extent = [
    center[0] - maxSideLengthInMeters / 2,
    center[1] - maxSideLengthInMeters / 2,
    center[0] + maxSideLengthInMeters / 2,
    center[1] + maxSideLengthInMeters / 2,
  ];
  const polygon = polygonFromExtent(extent);
  polygon.transform('EPSG:3857', 'EPSG:4326');
  return polygon;
}

export function getMinSquare(coords) {
  return polygonFromExtent(getMinExtent(coords));
}

export function getMaxCircle(coords) {
  return new Circle(coords, MAX_RADIUS);
}

export function getMinCircle(coords) {
  return new Circle(coords, MIN_RADIUS);
}

const GEOJSON = new GeoJSON();
export const toGeoJSON = (geom, reproject = false) =>
  GEOJSON.writeGeometryObject(
    geom,
    reproject
      ? {featureProjection: 'EPSG:3857', dataProjection: 'EPSG:4326'}
      : undefined,
  );

export const isGeometryTooLarge = area => area / MAX_GEOMETRY_AREA >= 1.1;

export const computeGeometryTooLarge = geometry => {
  const area = getArea(geometry, {projection: GEO_PROJECTION}) / 1e6;
  const geometryTooLarge = isGeometryTooLarge(area);
  return {geometryTooLarge, area};
};

export const createGeometryObj = geometry => {
  let newGeometry;
  let geometryTooLarge = true;
  let center;
  const formatGeoJSON = new GeoJSON();

  if (geometry) {
    const olGeom = formatGeoJSON.readGeometry(geometry);
    newGeometry = olGeom.simplify(0.00000001);
    const {geometryTooLarge: isGeometryTooLarge, area} =
      computeGeometryTooLarge(olGeom);
    geometryTooLarge = isGeometryTooLarge;

    if (geometryTooLarge) {
      trackEvent('Geometry defined - large AOI', {
        area,
        units: 'sqkm',
      });
    }

    center = getCenter(olGeom.getExtent());
    newGeometry = formatGeoJSON.writeGeometryObject(newGeometry, {
      decimals: 8,
    });
  } else {
    newGeometry = geometry;
  }

  return {newGeometry, geometryTooLarge, center};
};
